import { createFileRoute, Link } from '@tanstack/react-router'
import { Plus, Search, Filter, ChevronDown, Users, Settings2, ListTodo } from 'lucide-react'
import { useState } from 'react'

import {
  EmptyState,
  NoResultsEmptyState,
  determineEmptyStateType,
} from '@/components/common/empty-state'
import { InlineStatusSelect } from '@/components/common/inline-status-select'
import { BatchAssignmentDialog } from '@/components/pm/issue/batch-assignment-dialog'
import { BatchStageConfigDialog } from '@/components/pm/issue/batch-stage-config-dialog'
import { IssueForm } from '@/components/pm/issue/issue-form'
import { QuickTimelogButton } from '@/components/pm/timelog/quick-timelog-button'
import { TimelogPromptDialog, useTimelogPrompt } from '@/components/pm/timelog/timelog-prompt-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/components/ui/toast'
import {
  useIssues,
  useUpdateIssueStatus,
  type IssueStatus,
  type IssueType,
  type IssuePriority,
  type IssueListParams,
  type Issue,
 useProjects, type DeveloperGroup } from '@/hooks'
import { cn } from '@/lib/utils'

/**
 * 全局任务列表页面
 */
export const Route = createFileRoute('/_authenticated/pm/issues/')({
  component: IssueListPage,
})

const STATUS_LABELS: Record<IssueStatus, string> = {
  backlog: '待办',
  todo: '计划中',
  in_progress: '进行中',
  code_review: '代码审查',
  testing: '测试中',
  done: '已完成',
  blocked: '已阻塞',
  cancelled: '已取消',
}

const TYPE_LABELS: Record<IssueType, string> = {
  epic: '史诗',
  story: '故事',
  task: '任务',
  bug: '缺陷',
}

const TYPE_COLORS: Record<IssueType, string> = {
  epic: 'text-purple-600',
  story: 'text-green-600',
  task: 'text-blue-600',
  bug: 'text-red-600',
}

const PRIORITY_LABELS: Record<IssuePriority, string> = {
  urgent: '紧急',
  high: '高',
  medium: '中',
  low: '低',
}

const PRIORITY_COLORS: Record<IssuePriority, string> = {
  urgent: 'text-red-600',
  high: 'text-orange-600',
  medium: 'text-yellow-600',
  low: 'text-gray-600',
}

const DEVELOPER_GROUP_LABELS: Record<DeveloperGroup, string> = {
  product: '产品',
  design: '设计',
  frontend: '前端',
  backend: '后端',
  testing: '测试',
}

function IssueListPage() {
  const [filters, setFilters] = useState<IssueListParams>({
    page: 1,
    pageSize: 20,
  })
  const [searchInput, setSearchInput] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [selectedProjectId, setSelectedProjectId] = useState<string>('')
  const [selectedIssueIds, setSelectedIssueIds] = useState<Set<string>>(new Set())
  const [showBatchAssign, setShowBatchAssign] = useState(false)
  const [showBatchStageConfig, setShowBatchStageConfig] = useState(false)

  const { data: issues, isLoading, error } = useIssues(filters)
  const { data: projects } = useProjects()
  const updateStatusMutation = useUpdateIssueStatus()
  const toast = useToast()
  const { promptState, showPrompt, setPromptOpen } = useTimelogPrompt()

  // 处理状态更新
  const handleStatusChange = async (issue: Issue, newStatus: IssueStatus) => {
    await updateStatusMutation.mutateAsync({
      id: issue.id,
      data: { status: newStatus },
    })
  }

  const handleSearch = () => {
    setFilters((prev) => ({ ...prev, search: searchInput, page: 1 }))
  }

  const handleStatusFilter = (status: IssueStatus | undefined) => {
    setFilters((prev) => ({ ...prev, status, page: 1 }))
  }

  const handleTypeFilter = (type: IssueType | undefined) => {
    setFilters((prev) => ({ ...prev, type, page: 1 }))
  }

  const handlePriorityFilter = (priority: IssuePriority | undefined) => {
    setFilters((prev) => ({ ...prev, priority, page: 1 }))
  }

  const handleDeveloperGroupFilter = (developerGroup: DeveloperGroup | undefined) => {
    setFilters((prev) => ({ ...prev, developerGroup, page: 1 }))
  }

  const toggleSelectIssue = (issueId: string) => {
    setSelectedIssueIds((prev) => {
      const next = new Set(prev)
      if (next.has(issueId)) {
        next.delete(issueId)
      } else {
        next.add(issueId)
      }
      return next
    })
  }

  const toggleSelectAll = () => {
    if (!issues) {return}
    if (selectedIssueIds.size === issues.length) {
      setSelectedIssueIds(new Set())
    } else {
      setSelectedIssueIds(new Set(issues.map((i) => i.id)))
    }
  }

  const selectedIssues = issues?.filter((i) => selectedIssueIds.has(i.id)) || []

  // 获取选中任务的项目ID（批量分配需要同一项目）
  const selectedProjectIds = new Set(selectedIssues.map((i) => i.projectId))
  const canBatchAssign = selectedIssues.length > 0 && selectedProjectIds.size === 1
  const batchAssignProjectId = canBatchAssign ? selectedIssues[0].projectId : ''

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">任务列表</h1>
          <p className="text-muted-foreground">查看和管理所有任务</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          新建任务
        </button>
      </div>

      {/* 新建任务表单 - 需要先选择项目 */}
      {showCreateForm && (
        selectedProjectId ? (
          <IssueForm
            projectId={selectedProjectId}
            onClose={() => {
              setShowCreateForm(false)
              setSelectedProjectId('')
            }}
            onSuccess={() => {
              setShowCreateForm(false)
              setSelectedProjectId('')
            }}
          />
        ) : (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg">
              <h3 className="mb-4 text-lg font-semibold">选择项目</h3>
              <p className="mb-4 text-sm text-muted-foreground">请先选择要创建任务的项目</p>
              {projects && projects.length > 0 ? (
                <div className="space-y-2">
                  {projects.map((project) => (
                    <button
                      key={project.id}
                      onClick={() => setSelectedProjectId(project.id)}
                      className="w-full rounded-md border p-3 text-left hover:bg-muted"
                    >
                      <div className="font-medium">{project.name}</div>
                      {project.description && (
                        <div className="text-sm text-muted-foreground line-clamp-1">
                          {project.description}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">暂无可用项目，请先创建项目</p>
              )}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="rounded-md px-4 py-2 text-sm font-medium hover:bg-muted"
                >
                  取消
                </button>
              </div>
            </div>
          </div>
        )
      )}

      {/* 批量分配对话框 */}
      {showBatchAssign && canBatchAssign && (
        <BatchAssignmentDialog
          issues={selectedIssues}
          projectId={batchAssignProjectId}
          onClose={() => setShowBatchAssign(false)}
          onSuccess={() => {
            setShowBatchAssign(false)
            setSelectedIssueIds(new Set())
          }}
        />
      )}

      {/* 批量阶段配置对话框 */}
      {showBatchStageConfig && canBatchAssign && (
        <BatchStageConfigDialog
          issues={selectedIssues}
          projectId={batchAssignProjectId}
          onClose={() => setShowBatchStageConfig(false)}
          onSuccess={() => {
            setShowBatchStageConfig(false)
            setSelectedIssueIds(new Set())
          }}
        />
      )}

      {/* 搜索和筛选 */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* 搜索框 */}
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="搜索任务..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch()
                }
              }}
              className="w-full rounded-md border bg-background py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* 筛选按钮 */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              'inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
              showFilters ? 'bg-muted' : 'hover:bg-muted'
            )}
          >
            <Filter className="h-4 w-4" />
            筛选
            <ChevronDown className={cn('h-4 w-4 transition-transform', showFilters && 'rotate-180')} />
          </button>

          {/* 批量操作按钮 */}
          {selectedIssueIds.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                已选择 {selectedIssueIds.size} 项
              </span>
              <button
                onClick={() => setShowBatchAssign(true)}
                disabled={!canBatchAssign}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                title={!canBatchAssign ? '批量分配需要选择同一项目的任务' : undefined}
              >
                <Users className="h-4 w-4" />
                批量分配
              </button>
              <button
                onClick={() => setShowBatchStageConfig(true)}
                disabled={!canBatchAssign}
                className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
                title={!canBatchAssign ? '批量配置需要选择同一项目的任务' : undefined}
              >
                <Settings2 className="h-4 w-4" />
                阶段配置
              </button>
              <button
                onClick={() => setSelectedIssueIds(new Set())}
                className="rounded-md px-3 py-2 text-sm font-medium hover:bg-muted"
              >
                取消选择
              </button>
            </div>
          )}
        </div>

        {/* 筛选面板 */}
        {showFilters && (
          <div className="rounded-lg border bg-card p-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {/* 状态筛选 */}
              <div>
                <label className="mb-2 block text-sm font-medium">状态</label>
                <Select
                  value={(filters.status as string) ?? 'all'}
                  onValueChange={(value) => handleStatusFilter(value === 'all' ? undefined : value as IssueStatus)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="全部" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部</SelectItem>
                    {(Object.keys(STATUS_LABELS) as IssueStatus[]).map((status) => (
                      <SelectItem key={status} value={status}>
                        {STATUS_LABELS[status]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 类型筛选 */}
              <div>
                <label className="mb-2 block text-sm font-medium">类型</label>
                <Select
                  value={(filters.type as string) ?? 'all'}
                  onValueChange={(value) => handleTypeFilter(value === 'all' ? undefined : value as IssueType)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="全部" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部</SelectItem>
                    {(Object.keys(TYPE_LABELS) as IssueType[]).map((type) => (
                      <SelectItem key={type} value={type}>
                        {TYPE_LABELS[type]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 优先级筛选 */}
              <div>
                <label className="mb-2 block text-sm font-medium">优先级</label>
                <Select
                  value={(filters.priority as string) ?? 'all'}
                  onValueChange={(value) => handlePriorityFilter(value === 'all' ? undefined : value as IssuePriority)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="全部" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部</SelectItem>
                    {(Object.keys(PRIORITY_LABELS) as IssuePriority[]).map((priority) => (
                      <SelectItem key={priority} value={priority}>
                        {PRIORITY_LABELS[priority]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 开发组筛选 */}
              <div>
                <label className="mb-2 block text-sm font-medium">开发组</label>
                <Select
                  value={(filters.developerGroup as string) ?? 'all'}
                  onValueChange={(value) => handleDeveloperGroupFilter(value === 'all' ? undefined : value as DeveloperGroup)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="全部" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部</SelectItem>
                    {(Object.keys(DEVELOPER_GROUP_LABELS) as DeveloperGroup[]).map((group) => (
                      <SelectItem key={group} value={group}>
                        {DEVELOPER_GROUP_LABELS[group]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 任务列表 */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      ) : !issues || issues.length === 0 ? (
        // Requirements 4.1, 4.2, 4.3, 4.4 - 使用增强空状态组件
        (() => {
          const hasFilters = Boolean(
            filters.status || filters.type || filters.priority || 
            filters.developerGroup || filters.search
          )
          const emptyStateType = determineEmptyStateType({
            hasData: false,
            hasFilters,
            hasError: false,
            isFirstVisit: false, // 任务列表通常不是首次使用场景
          })
          
          if (emptyStateType === 'no-results') {
            return (
              <NoResultsEmptyState
                title="未找到匹配的任务"
                description="尝试调整筛选条件或搜索关键词"
                onClearFilters={() => {
                  setFilters({ page: 1, pageSize: 20 })
                  setSearchInput('')
                }}
              />
            )
          }
          
          return (
            <EmptyState
              type={emptyStateType}
              title="暂无任务"
              description="点击上方按钮创建第一个任务"
              icon={ListTodo}
              primaryAction={{
                label: '新建任务',
                onClick: () => setShowCreateForm(true),
              }}
            />
          )
        })()
      ) : (
        <div className="rounded-lg border">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedIssueIds.size === issues.length && issues.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded"
                  />
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">编号</th>
                <th className="px-4 py-3 text-left text-sm font-medium">标题</th>
                <th className="px-4 py-3 text-left text-sm font-medium">类型</th>
                <th className="px-4 py-3 text-left text-sm font-medium">状态</th>
                <th className="px-4 py-3 text-left text-sm font-medium">优先级</th>
                <th className="px-4 py-3 text-left text-sm font-medium">截止日期</th>
                <th className="px-4 py-3 text-left text-sm font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {issues.map((issue) => (
                <tr key={issue.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIssueIds.has(issue.id)}
                      onChange={() => toggleSelectIssue(issue.id)}
                      className="rounded"
                    />
                  </td>
                  <td className="px-4 py-3 text-sm font-mono">{issue.code}</td>
                  <td className="px-4 py-3">
                    <Link
                      to="/pm/issues/$issueId"
                      params={{ issueId: issue.id }}
                      className="font-medium hover:text-primary hover:underline"
                    >
                      {issue.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn('text-sm font-medium', TYPE_COLORS[issue.type])}>
                      {TYPE_LABELS[issue.type]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <InlineStatusSelect
                      value={issue.status}
                      onChange={(newStatus) => handleStatusChange(issue, newStatus)}
                      onSaveSuccess={() => {
                        toast.success('状态已更新')
                      }}
                      onSaveError={(error) => {
                        toast.error('更新失败', error.message)
                      }}
                      onStatusDone={() => {
                        showPrompt({
                          id: issue.id,
                          title: issue.title,
                          code: issue.code,
                        })
                      }}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn('text-sm font-medium', PRIORITY_COLORS[issue.priority])}>
                      {PRIORITY_LABELS[issue.priority]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {issue.dueDate
                      ? new Date(issue.dueDate).toLocaleDateString('zh-CN')
                      : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <QuickTimelogButton
                      issueId={issue.id}
                      issueName={issue.title}
                      projectId={issue.projectId}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 工时记录提示对话框 */}
      <TimelogPromptDialog
        open={promptState.open}
        onOpenChange={setPromptOpen}
        issueId={promptState.issueId}
        issueTitle={promptState.issueTitle}
        issueCode={promptState.issueCode}
      />
    </div>
  )
}
